import { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { toast } from 'sonner';
import { Barcode, Copy, Printer, Search, X, ImageOff, Check } from 'lucide-react';

const BarcodeManager = ({ isOpen, onClose }) => {
  const { appContext } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  if (!isOpen) return null;

  const products = appContext.product || [];
  const filteredProducts = searchQuery
    ? products.filter(p =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.p_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.barcode?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  const copyBarcode = async (barcode, id) => {
    try {
      await navigator.clipboard.writeText(barcode);
      setCopiedId(id);
      toast.success('Barcode copied!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const toggleSelect = (id) => {
    setSelectedProducts(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    const allIds = filteredProducts.map(p => p.id);
    setSelectedProducts(allIds);
  };

  const clearSelection = () => {
    setSelectedProducts([]);
  };

  const printBarcodes = () => {
    const productsToPrint = products.filter(p => selectedProducts.includes(p.id));
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Barcodes - SND Inventory</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; }
          .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .barcode-card {
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px;
            text-align: center;
            page-break-inside: avoid;
          }
          .product-name { font-size: 12px; font-weight: 600; margin-bottom: 8px; }
          .product-sku { font-size: 10px; color: #64748b; margin-bottom: 10px; }
          svg { max-width: 100%; height: auto; }
          @media print {
            body { padding: 10px; }
            .grid { gap: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="grid">
          ${productsToPrint.map(p => `
            <div class="barcode-card">
              <div class="product-name">${p.name}</div>
              <div class="product-sku">${p.p_id}</div>
              <svg id="barcode-${p.id}"></svg>
            </div>
          `).join('')}
        </div>
        <script>
          ${productsToPrint.map(p => `
            try {
              JsBarcode("#barcode-${p.id}", "${p.barcode || p.p_id}", {
                format: "CODE128",
                width: 1.5,
                height: 50,
                displayValue: true,
                fontSize: 12
              });
            } catch(e) { console.error(e); }
          `).join('')}
          setTimeout(() => window.print(), 500);
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Barcode className="w-5 h-5 text-emerald-600" />
            Barcode Manager
          </h3>
          <button onClick={onClose} className="btn-icon text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Actions */}
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={selectAll} className="btn-secondary text-sm py-2">
              Select All ({filteredProducts.length})
            </button>
            {selectedProducts.length > 0 && (
              <>
                <button onClick={clearSelection} className="btn-ghost text-sm py-2">
                  Clear ({selectedProducts.length})
                </button>
                <button onClick={printBarcodes} className="btn-primary text-sm py-2">
                  <Printer className="w-4 h-4" />
                  Print Selected
                </button>
              </>
            )}
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredProducts.slice(0, 100).map((product) => (
              <div
                key={product.id}
                className={`card p-3 cursor-pointer transition-all ${
                  selectedProducts.includes(product.id) ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''
                }`}
                onClick={() => toggleSelect(product.id)}
              >
                <div className="flex items-start gap-3">
                  {product.image ? (
                    <img src={product.image} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ImageOff className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate">{product.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{product.p_id}</p>
                    {product.barcode && (
                      <div className="flex items-center gap-1 mt-1">
                        <Barcode className="w-3 h-3 text-slate-400" />
                        <span className="text-xs font-mono text-slate-600">{product.barcode}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyBarcode(product.barcode, product.id);
                          }}
                          className="ml-auto p-1 hover:bg-slate-200 rounded"
                        >
                          {copiedId === product.id ? (
                            <Check className="w-3 h-3 text-emerald-600" />
                          ) : (
                            <Copy className="w-3 h-3 text-slate-400" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filteredProducts.length > 100 && (
            <p className="text-center text-sm text-slate-500 mt-4">
              Showing 100 of {filteredProducts.length} products. Use search to find more.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeManager;
